import { ReactNode } from "react";
import { useSubscription } from "@/hooks/useSubscription";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { StrataLogo } from "@/components/StrataLogo";

interface FeatureGateProps {
  children: ReactNode;
  feature: "has_forms" | "has_advanced_views" | "has_agent" | "has_sso";
  workspaceId: string;
  fallback?: ReactNode;
  /** If true, show upgrade prompt instead of hiding */
  showPrompt?: boolean;
}

const FEATURE_NAMES: Record<string, { name: string; description: string; minPlan: string }> = {
  has_forms: {
    name: "Forms",
    description: "Create shareable forms that feed directly into your tables",
    minPlan: "Starter",
  },
  has_advanced_views: {
    name: "Advanced Views",
    description: "Kanban, Calendar, Gallery, and more visualization options",
    minPlan: "Starter",
  },
  has_agent: {
    name: "AI Agent",
    description: "Autonomous AI that learns your patterns and suggests improvements",
    minPlan: "Pro",
  },
  has_sso: {
    name: "SSO & 2FA",
    description: "Enterprise-grade security with single sign-on",
    minPlan: "Pro",
  },
};

export function FeatureGate({
  children,
  feature,
  workspaceId,
  fallback,
  showPrompt = true,
}: FeatureGateProps) {
  const { hasFeature, loading } = useSubscription(workspaceId);
  const navigate = useNavigate();

  // While loading, show children (optimistic)
  if (loading) return <>{children}</>;

  // Feature is available
  if (hasFeature(feature)) return <>{children}</>;

  // Feature not available - show fallback or upgrade prompt
  if (fallback) return <>{fallback}</>;

  if (!showPrompt) return null;

  const featureInfo = FEATURE_NAMES[feature];

  return (
    <Card className="border-dashed border-2 border-primary/30 bg-primary/5">
      <CardHeader className="text-center pb-2">
        <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
          <Lock className="h-5 w-5 text-primary" />
        </div>
        <CardTitle className="text-lg">{featureInfo.name}</CardTitle>
        <CardDescription>{featureInfo.description}</CardDescription>
      </CardHeader>
      <CardContent className="text-center space-y-4">
        <p className="text-sm text-muted-foreground">
          Available on <span className="font-semibold text-primary">{featureInfo.minPlan}</span> plan and above
        </p>
        <Button onClick={() => navigate("/settings?tab=billing")} className="gap-2">
          <StrataLogo size="sm" />
          Upgrade to Unlock
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * Usage Limit Gate - shows remaining usage and upgrade prompt
 */
interface UsageLimitProps {
  current: number;
  limit: number;
  label: string;
  workspaceId: string;
}

export function UsageLimitIndicator({ current, limit, label, workspaceId }: UsageLimitProps) {
  const navigate = useNavigate();
  const isUnlimited = limit === -1;
  const percentage = isUnlimited ? 0 : Math.min((current / limit) * 100, 100);
  const isNearLimit = percentage >= 80;
  const isAtLimit = percentage >= 100;

  if (isUnlimited) {
    return (
      <div className="text-xs text-muted-foreground">
        {current} {label} used · Unlimited
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className={isAtLimit ? "text-destructive font-medium" : "text-muted-foreground"}>
          {current}/{limit} {label}
        </span>
        {isNearLimit && !isAtLimit && (
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs text-primary"
            onClick={() => navigate("/settings?tab=billing")}
          >
            Upgrade
          </Button>
        )}
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full transition-all ${
            isAtLimit ? "bg-destructive" : isNearLimit ? "bg-amber-500" : "bg-primary"
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {isAtLimit && (
        <Button
          variant="destructive"
          size="sm"
          className="w-full h-7 text-xs mt-2"
          onClick={() => navigate("/settings?tab=billing")}
        >
          Limit Reached - Upgrade Now
        </Button>
      )}
    </div>
  );
}
